import { useState, useEffect, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
const EstimateFlow = lazy(() =>
  import("@/components/EstimateFlow").then((m) => ({
    default: m.EstimateFlow,
  })),
);
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { CheckCircle2, ArrowRight, Star, MapPin } from "lucide-react";
import { projects } from "./GalleryPage";

const galleryImages = [
  "https://vibe.filesafe.space/1781836485235618884/attachments/efbcb447-1d5c-4e17-a244-be49024103de.jpg",
  "https://vibe.filesafe.space/1781836485235618884/attachments/fb0dffe3-e2fc-4f95-9d53-985d5dda16ba.jpg",
  "https://vibe.filesafe.space/1781836485235618884/attachments/25f4ec04-dc91-4e90-9d64-545e20ec00a8.jpg",
  "https://vibe.filesafe.space/1781836485235618884/attachments/3112f996-729e-43ce-b731-b7cfaac12aae.jpg",
];

export default function LocationPage() {
  const { locationId } = useParams();
  const [isEstimateOpen, setIsEstimateOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  useEffect(() => {
    const handleOpenEstimate = (e: Event) => {
      setIsEstimateOpen(true);
    };
    window.addEventListener("open-estimate", handleOpenEstimate);
    return () =>
      window.removeEventListener("open-estimate", handleOpenEstimate);
  }, []);

  const cityName = locationId
    ? locationId
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "Your City";

  const getNeighborhoods = (city: string) => {
    const defaultNeighborhoods = "and surrounding neighborhoods";
    const neighborhoodsMap: Record<string, string> = {
      Greensboro:
        "Fisher Park, Lindley Park, Sunset Hills, Irving Park, and surrounding neighborhoods",
      Raleigh:
        "North Hills, Brier Creek, Oakwood, Cameron Village, and surrounding neighborhoods",
      Durham:
        "Trinity Park, Hope Valley, Woodcroft, Downtown Durham, and surrounding neighborhoods",
      "Chapel Hill":
        "Meadowmont, Southern Village, Governors Club, and surrounding neighborhoods",
      Cary: "Preston, Lochmere, MacGregor Downs, Amberly, and surrounding neighborhoods",
      "Wake Forest":
        "Heritage, Hasentree, Wakefield, and surrounding neighborhoods",
      "High Point": "Emerywood, Deep River, and surrounding neighborhoods",
      "Winston Salem":
        "Buena Vista, Ardmore, West End, and surrounding neighborhoods",
    };
    return neighborhoodsMap[city] || defaultNeighborhoods;
  };

  const neighborhoods = getNeighborhoods(cityName);

  const getLocationSEO = (city: string) => {
    const defaultSEO = {
      title: `Top-Rated Home Remodeling in ${city}, NC | Long Vista Remodeling & Exteriors`,
      description: `Long Vista Remodeling serves ${city} homeowners with roofing, kitchens, bathrooms & more. Call for a free estimate today.`,
    };
    return defaultSEO;
  };

  const seo = getLocationSEO(cityName);

  useEffect(() => {
    if (cityName) {
      document.title = seo.title;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement("meta");
        metaDescription.setAttribute("name", "description");
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute("content", seo.description);
    }
  }, [cityName, seo]);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Main Hero Section - Rebuilt */}
      <section className="relative w-full overflow-hidden bg-white pt-4 pb-12 lg:pt-6 lg:pb-20">
        <div className="absolute inset-0 z-0 flex justify-end">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/80 to-white/10 lg:bg-gradient-to-r lg:from-white lg:via-white/95 lg:to-transparent z-10"></div>
        </div>

        <div className="container relative z-20 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-4 lg:pt-8">
            {/* Left Column */}
            <div className="flex flex-col text-left lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm mb-4 w-fit">
                <MapPin className="h-4 w-4" />
                Serving {cityName}, NC & Surrounding Areas
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 tracking-tight mb-6 leading-[1.1]">
                From Roof to Floor,
                <br />
                <span className="text-primary">We Handle It All.</span>
              </h1>

              <p className="text-lg md:text-xl text-gray-900 mb-6 max-w-xl font-medium">
                Long Vista Remodeling handles every part of your project in{" "}
                {cityName} from start to finish so you never have to juggle
                multiple contractors again.
              </p>

              {/* Service Badges */}
              <div className="flex flex-wrap gap-2 mb-8">
                {[
                  {
                    label: "Roofing",
                    slug: "roof-repair-shingle-installation",
                  },
                  { label: "Kitchen Remodels", slug: "kitchen-remodeling" },
                  { label: "Bathrooms", slug: "bathroom-remodeling" },
                  { label: "Flooring", slug: "flooring-vinyl-lvp-tile-" },
                  { label: "Gutters", slug: "gutters" },
                  { label: "Gutter Guards", slug: "gutter-guards" },
                  { label: "Concrete", slug: "concrete" },
                  { label: "Shingles", slug: "shingles" },
                  {
                    label: "Windows & Doors",
                    slug: "window-door-installation",
                  },
                  { label: "Decks & Porches", slug: "porches-decks" },
                  { label: "Sunrooms", slug: "sunrooms" },
                  { label: "Painting", slug: "interior-exterior-painting" },
                ].map((service) => (
                  <Link
                    key={service.label}
                    to={`/services/${service.slug}`}
                    className="px-4 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-gray-800 text-sm font-medium hover:bg-gray-100 transition-colors"
                  >
                    {service.label}
                  </Link>
                ))}
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-10 flex-wrap">
                <Button
                  asChild
                  className="bg-primary hover:bg-primary/90 text-white text-lg px-8 py-6 rounded-lg font-bold transition-all w-full sm:w-auto shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  <a href="tel:9198992094">Call Now</a>
                </Button>
                <Button
                  onClick={() => setIsEstimateOpen(true)}
                  variant="outline"
                  className="lg:hidden bg-white hover:bg-gray-50 text-[#0588c0] border-2 border-[#0588c0] text-lg px-8 py-6 rounded-lg font-bold transition-all w-full sm:w-auto shadow-sm"
                >
                  Start Interactive Quote
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="bg-white hover:bg-gray-50 text-gray-900 border-2 border-gray-200 text-lg px-8 py-6 rounded-lg font-bold transition-all w-full sm:w-auto shadow-sm"
                >
                  <a href="#gallery">View Our Work</a>
                </Button>
              </div>

              {/* Checkmarks */}
              <div className="grid grid-cols-2 gap-y-4 gap-x-2 text-sm text-gray-800 font-medium">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Satisfaction
                  Guaranteed
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Free
                  Estimates
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Serving{" "}
                  {cityName}
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="hidden lg:flex lg:col-span-5 justify-end items-center lg:translate-x-6 xl:translate-x-12">
              <div className="bg-white rounded-3xl p-10 sm:p-12 w-full max-w-xl shadow-2xl relative z-10 border border-gray-100">
                <div className="mb-10 text-center flex flex-col items-center">
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">
                    Interactive <span className="text-primary">Quote</span> Form
                  </h3>
                  <p className="text-lg text-gray-600 mx-auto max-w-[400px] leading-relaxed">
                    Answer a few quick questions about your project and we'll
                    send your personalized estimate within 24 hours.
                  </p>
                </div>
                <div className="w-full">
                  <Button
                    onClick={() => setIsEstimateOpen(true)}
                    className="w-full bg-primary hover:bg-primary/90 text-white h-16 text-xl font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
                  >
                    Start Interactive Quote
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Work Carousel Section */}
      <section className="bg-[#0a0a0a] py-12 lg:py-20 border-t border-white/5 overflow-hidden pt-[42px] pb-[42px]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 lg:mb-12 gap-4">
            <div>
              <span className="text-sm font-bold tracking-widest text-primary uppercase mb-3 block">
                LATEST WORK IN {cityName.toUpperCase()}
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                Recent Transformations
              </h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="bg-transparent border-white/20 text-white hover:bg-white/10 hidden md:flex"
            >
              <Link to="/gallery">View All Projects</Link>
            </Button>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 md:-ml-6">
              {[...projects]
                .reverse()
                .slice(0, 6)
                .map((project, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3"
                  >
                    <Link
                      to="/gallery"
                      className="block bg-white/5 border border-white/10 rounded-2xl overflow-hidden group cursor-pointer"
                    >
                      <div className="relative h-64 md:h-72 overflow-hidden">
                        <img
                          src={project.afterImage}
                          alt={project.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-90 transition-opacity group-hover:opacity-100" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-bold text-white mb-1">
                            {project.title}
                          </h3>
                          <div className="flex items-center text-gray-300 text-sm gap-3">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />{" "}
                              {project.location}
                            </span>
                            <span>•</span>
                            <span>{project.date}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </CarouselItem>
                ))}
            </CarouselContent>
            <div className="flex justify-center md:justify-end gap-2 mt-0.5">
              <CarouselPrevious className="static translate-x-0 translate-y-0 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white h-12 w-12" />
              <CarouselNext className="static translate-x-0 translate-y-0 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white h-12 w-12" />
            </div>
          </Carousel>

          <div className="mt-8 md:hidden">
            <Button
              asChild
              variant="outline"
              className="w-full bg-transparent border-white/20 text-white hover:bg-white/10"
            >
              <Link to="/gallery">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="pt-6 pb-6 lg:pt-12 lg:pb-10 bg-gray-50 border-t border-gray-200 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
              What Our Clients Say
            </h2>
          </div>

          <div className="w-full relative z-10 mt-2 max-w-6xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: false,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4 md:-ml-6">
                {/* Google Review */}
                <CarouselItem className="pl-4 md:pl-6 md:basis-1/2">
                  <div className="h-full w-full bg-white rounded-2xl p-4 md:p-8 shadow-lg border border-gray-100 text-left relative">
                    <a
                      href="https://share.google/yOBVljcCT0kCgAhtx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-4 right-4 md:top-8 md:right-8 hover:opacity-80 transition-opacity"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                        alt="Google Review"
                        className="w-5 h-5 md:w-8 md:h-8 object-contain"
                      />
                    </a>
                    <div className="flex items-center gap-3 mb-3 md:mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg md:text-xl shrink-0">
                        H
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-base md:text-lg">
                          Hassan Ismail
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className="w-3 h-3 md:w-4 md:h-4 fill-yellow-400 text-yellow-400"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm md:text-lg leading-relaxed">
                      I had a rain leak in my ceiling that had been giving me
                      problems for almost 6 months. I was worried it would be a
                      huge project but Chris came out, assessed everything
                      quickly, gave me a free estimate, and had the ceiling
                      fully replaced within a couple of days. The work was
                      clean, professional, and you'd never know there was ever a
                      problem. Excellent service, would definitely recommend,
                      Genuinely one of the most professional contractors I've
                      dealt wit
                    </p>
                    <div className="flex flex-wrap gap-2 md:gap-3 mt-4 md:mt-5">
                      {galleryImages.map((src, index) => (
                        <button
                          key={index}
                          className="w-12 h-12 md:w-20 md:h-20 rounded-xl overflow-hidden border border-gray-200 hover:border-primary transition-colors shadow-sm flex-shrink-0"
                          onClick={() => {
                            setInitialSlide(index);
                            setGalleryOpen(true);
                          }}
                          aria-label={`View project photo ${index + 1}`}
                        >
                          <img
                            src={src}
                            alt={`Project photo ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </CarouselItem>

                {/* Yelp Review */}
                <CarouselItem className="pl-4 md:pl-6 md:basis-1/2">
                  <div className="h-full w-full bg-white rounded-2xl p-4 md:p-8 shadow-lg border border-gray-100 text-left relative">
                    <a
                      href="https://www.yelp.com/biz/long-vista-remodeling-and-exteriors-raleigh"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-4 right-4 md:top-8 md:right-8 hover:opacity-80 transition-opacity"
                    >
                      <img
                        src="https://vibe.filesafe.space/1781836485235618884/attachments/321f6708-b549-412c-8163-a3a35980ba39.png"
                        alt="Yelp Review"
                        className="w-10 h-5 md:w-16 md:h-8 object-contain"
                      />
                    </a>
                    <div className="flex items-center gap-3 mb-3 md:mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold text-lg md:text-xl shrink-0">
                        R
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-base md:text-lg">
                          Roy
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className="w-3 h-3 md:w-4 md:h-4 fill-red-500 text-red-500"
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500 font-medium">
                            7/8/26
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm md:text-lg leading-relaxed">
                      These guys are great and very reasonable with their
                      prices--I would recommend them to anyone who needs them to
                      help out with their project
                    </p>
                  </div>
                </CarouselItem>

                {/* Don Morais Google Review */}
                <CarouselItem className="pl-4 md:pl-6 md:basis-1/2">
                  <div className="h-full w-full bg-white rounded-2xl p-4 md:p-8 shadow-lg border border-gray-100 text-left relative">
                    <a
                      href="https://share.google/yOBVljcCT0kCgAhtx"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute top-4 right-4 md:top-8 md:right-8 hover:opacity-80 transition-opacity"
                    >
                      <img
                        src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg"
                        alt="Google Review"
                        className="w-5 h-5 md:w-8 md:h-8 object-contain"
                      />
                    </a>
                    <div className="flex items-center gap-3 mb-3 md:mb-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-lg md:text-xl shrink-0">
                        D
                      </div>
                      <div>
                        <div className="font-bold text-gray-900 text-base md:text-lg">
                          Don Morais
                        </div>
                        <div className="flex items-center gap-1 mt-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className="w-3 h-3 md:w-4 md:h-4 fill-yellow-400 text-yellow-400"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm md:text-lg leading-relaxed">
                      Very prompt and reasonable. I definitely will use again in
                      the future and recommend!
                    </p>
                  </div>
                </CarouselItem>
              </CarouselContent>

              <div className="flex justify-center gap-4 mt-4">
                <CarouselPrevious className="static translate-x-0 translate-y-0 bg-white border-gray-200 text-gray-900 hover:bg-gray-50 h-10 w-10 md:h-12 md:w-12 shadow-sm" />
                <CarouselNext className="static translate-x-0 translate-y-0 bg-white border-gray-200 text-gray-900 hover:bg-gray-50 h-10 w-10 md:h-12 md:w-12 shadow-sm" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>

      {/* Secondary Hero Section (Original) */}
      <section className="relative bg-background pt-12 pb-6 md:pt-16 lg:pt-20 lg:pb-8 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col justify-center max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-[4rem] font-bold text-foreground tracking-tight mb-4 lg:mb-6 leading-[1.1]">
                Your Interactive Home Quote, Done Right.
              </h1>
              <p className="text-base md:text-xl text-muted-foreground mb-8 lg:mb-10 leading-relaxed">
                Answer a few quick questions about your project in {cityName}{" "}
                using our interactive quote form and we'll send you a
                personalized estimate within 24 hours. Need help? Our experts
                are just a call away.
              </p>
              <div className="mb-8 lg:mb-12">
                <Button
                  onClick={() => setIsEstimateOpen(true)}
                  className="rounded-full h-12 lg:h-14 px-8 lg:px-10 bg-primary hover:bg-primary/90 text-primary-foreground text-base lg:text-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  Start Interactive Quote{" "}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="hidden md:block relative w-full mt-8 md:mt-16 lg:mt-0 px-2 sm:px-0">
              <div className="relative w-full h-[320px] md:h-[500px] lg:h-[600px] rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl lg:ml-auto md:w-[85%] lg:w-[90%]">
                <img
                  src="https://vibe.filesafe.space/1781836485235618884/attachments/3f3bb805-4fd9-4df7-8ea7-be9d3becd22c.png"
                  alt={`Exterior Home Remodel and Roofing in ${cityName}`}
                  title={`exterior-home-remodel-roofing-${cityName.toLowerCase().replace(/\s+/g, "-")}.jpg`}
                  className="w-full h-full object-cover"
                  width="800"
                  height="600"
                  loading="lazy"
                  decoding="async"
                />
              </div>

              {/* Top Floating Card */}
              <div className="absolute -top-6 -left-2 md:-top-10 md:-left-12 lg:-top-12 lg:-left-16 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md md:shadow-xl w-[210px] md:w-[280px] animate-in fade-in slide-in-from-bottom-4 duration-300 z-10 border border-gray-100 transform-gpu">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
                  <img
                    src="https://vibe.filesafe.space/1781836485235618884/attachments/86f992d7-6c85-4ed4-a894-bb7611e6666b.png"
                    alt="Long Vista Remodeling Logo"
                    className="h-5 md:h-8 object-contain"
                    loading="lazy"
                  />
                </div>
                <h4 className="font-bold text-gray-900 mb-2 md:mb-4 leading-tight text-[13px] md:text-base">
                  Personalized estimates within 24 hours
                </h4>
                <div className="flex items-center gap-2 md:gap-4 mb-3 md:mb-5">
                  <img
                    src="https://vibe.filesafe.space/1781836485235618884/attachments/bff1ba5b-ea26-4f8b-a996-09df63755cf9.png"
                    className="w-8 h-8 md:w-12 md:h-12 rounded-lg object-cover"
                    alt="Kitchen Remodel Estimate"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-[10px] md:text-sm text-gray-600 font-medium">
                      Full Kitchen Remodel
                    </div>
                    <div className="font-bold text-primary text-sm md:text-lg">
                      $5,657
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setIsEstimateOpen(true)}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-9 md:h-11 text-xs md:text-base"
                >
                  Start Interactive Quote
                </Button>
              </div>

              {/* Bottom Floating Card */}
              <div className="absolute -bottom-6 -right-2 md:right-auto md:-bottom-10 md:-left-4 lg:-bottom-12 lg:-left-4 bg-white rounded-xl md:rounded-2xl p-4 md:p-6 shadow-md md:shadow-xl w-[210px] md:w-[280px] animate-in fade-in slide-in-from-bottom-4 duration-500 z-10 border border-gray-100 transform-gpu">
                <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-4">
                  <img
                    src="https://vibe.filesafe.space/1781836485235618884/attachments/86f992d7-6c85-4ed4-a894-bb7611e6666b.png"
                    alt="Long Vista Remodeling Logo"
                    className="h-5 md:h-8 object-contain"
                    loading="lazy"
                  />
                </div>
                <h4 className="font-bold text-gray-900 mb-2 md:mb-4 leading-tight text-[13px] md:text-base">
                  Interactive quote form for your project
                </h4>
                <div className="flex items-center gap-2 md:gap-4 mb-3 md:mb-5">
                  <img
                    src="https://vibe.filesafe.space/1781836485235618884/assets/a86a6342-6ba6-4f31-8fd8-49c205fef888.png"
                    className="w-8 h-8 md:w-12 md:h-12 rounded-lg object-cover"
                    alt="Bathroom Renovation Estimate"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-[10px] md:text-sm text-gray-600 font-medium">
                      Bathroom Renovation
                    </div>
                    <div className="font-bold text-primary text-sm md:text-lg">
                      $7,890
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setIsEstimateOpen(true)}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold h-9 md:h-11 text-xs md:text-base"
                >
                  Start Interactive Quote
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Before/After */}
      <section
        id="gallery"
        className="pt-10 pb-10 bg-white border-t border-gray-100"
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-sm font-bold tracking-widest text-[#0588c0] uppercase mb-4 block">
              OUR WORK IN {cityName.toUpperCase()}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              See the Long Vista Difference
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto text-sm md:text-base">
              Real projects. Real results. Every job finished to the same
              standard.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-8 mb-16">
            {projects.slice(1, 5).map((project, idx) => (
              <BeforeAfterSlider
                key={idx}
                beforeImage={project.beforeImage}
                afterImage={project.afterImage}
                title={project.title}
                location={project.location}
                date={project.date}
              />
            ))}
          </div>

          <div className="text-center mb-4">
            <Button
              asChild
              className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-lg text-lg transition-all"
            >
              <Link to="/gallery">View Full Gallery</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Areas We Service Section */}
      <section className="py-12 lg:py-16 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-sm font-bold tracking-widest text-[#0588c0] uppercase mb-4 block">
              SERVICE AREAS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Proudly Serving {cityName}
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto text-lg">
              We provide top-quality remodeling and exterior services across{" "}
              {cityName}, including {neighborhoods}.
            </p>
          </div>

          <div className="max-w-5xl mx-auto flex justify-center">
            <img
              src="https://vibe.filesafe.space/1781836485235618884/attachments/5a035a50-fa51-43c9-a48d-fc100582db3b.png"
              alt={`Map of North Carolina Service Areas including ${cityName}`}
              className="w-full h-auto object-contain"
              loading="lazy"
              decoding="async"
              width="1024"
              height="768"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="pt-12 lg:pt-16 pb-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://assets.cdn.filesafe.space/4lOsVXfRzB21f2Hlg8pa/media/6a35c7a71c5d711b359d1c20.jpg"
                alt={`Long Vista Remodeling General Contractors and Team on Job Site in ${cityName}`}
                title={`general-contractors-team-job-site-${cityName.toLowerCase().replace(/\s+/g, "-")}.jpg`}
                className="rounded-2xl shadow-lg object-cover w-full h-[500px]"
                loading="lazy"
                decoding="async"
                width="800"
                height="500"
              />
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-sm font-bold tracking-widest text-tertiary uppercase mb-4 block">
                WHO WE ARE
              </span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Built on Craftsmanship. Driven by Trust.
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Long Vista Remodeling and Exteriors was built on one simple
                belief — your home deserves to be treated with the same care you
                put into it. We are a locally owned and operated contracting
                company serving homeowners across {cityName}, {neighborhoods}.
                From a leaking roof to a full kitchen transformation, we handle
                every part of the job so you never have to coordinate between
                multiple contractors again. Every project we take on is personal
                to us. We show up on time, we communicate clearly, and we don't
                consider a job finished until you're completely satisfied with
                the result. One team. One standard. Every home.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-secondary/50 p-4 rounded-xl border border-border/50 text-center">
                  <div className="text-3xl font-bold text-foreground mb-1">
                    12
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Years of Experience
                  </div>
                </div>
                <div className="bg-secondary/50 p-4 rounded-xl border border-border/50 text-center">
                  <div className="text-3xl font-bold text-foreground mb-1">
                    240+
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Projects Completed
                  </div>
                </div>
                <div className="bg-secondary/50 p-4 rounded-xl border border-border/50 text-center">
                  <div className="text-3xl font-bold text-foreground mb-1">
                    28
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Cities Served
                  </div>
                </div>
                <div className="bg-secondary/50 p-4 rounded-xl border border-border/50 text-center">
                  <div className="text-3xl font-bold text-foreground mb-1">
                    100%
                  </div>
                  <div className="text-sm font-medium text-muted-foreground">
                    Satisfaction Guaranteed
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Transform Section */}
      <section className="bg-white py-10 lg:py-12 border-t border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Transform Your Home in {cityName}?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Answer a few quick questions about your project using our
            interactive quote form and we'll send you a personalized estimate
            within 24 hours.
          </p>
          <Button
            onClick={() => setIsEstimateOpen(true)}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            Start Interactive Quote
          </Button>
        </div>
      </section>

      <Suspense fallback={null}>
        <EstimateFlow
          isOpen={isEstimateOpen}
          onOpenChange={setIsEstimateOpen}
        />
      </Suspense>

      {/* Gallery Lightbox */}
      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-lg bg-black/95 p-6 border-none shadow-2xl [&>button]:text-white [&>button]:bg-black/50 [&>button]:p-1 [&>button]:rounded-full [&>button]:hover:bg-white/20">
          <DialogTitle className="sr-only">Project Gallery</DialogTitle>
          <div className="relative w-full flex items-center justify-center pt-4">
            <Carousel
              key={initialSlide}
              opts={{ startIndex: initialSlide, loop: true }}
              className="w-full"
            >
              <CarouselContent className="ml-0">
                {galleryImages.map((src, index) => (
                  <CarouselItem
                    key={index}
                    className="pl-0 flex items-center justify-center"
                  >
                    <img
                      src={src}
                      alt={`Gallery Image ${index + 1}`}
                      className="w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-1 bg-white/20 hover:bg-white/40 border-none text-white h-9 w-9" />
              <CarouselNext className="right-1 bg-white/20 hover:bg-white/40 border-none text-white h-9 w-9" />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
